import * as React from 'react'

import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import attendancePolicies from 'modules/attendance/store/collections/policies'
import attendanceSettings from 'modules/attendance/store/collections/settings'
import type AttendancePolicy from 'modules/attendance/store/models/AttendancePolicy'

interface Props {
  policy: AttendancePolicy
  showDeleteModal: () => void
}

const submitSetAsDefault = (policy: AttendancePolicy) => async () => {
  await policy.rpc('set_as_default')
  attendancePolicies.fetch()
}

const PolicyCard = ({ policy, showDeleteModal }: Props) => {
  const { active_employees: activeEmployees, id, default: defaultPolicy, name } = policy.attributes
  const attendanceSetting = attendanceSettings.currentByPolicy(id)
  const {
    desktop_clockin: desktopClockin,
    qr_code_clockin: qrCodeClockin,
    geolocation,
    face_recognition: faceRecognition,
    id_clock_shared_device: idClockSharedDevice,
  } = attendanceSetting?.attributes ?? {}
  const { grey, radical } = Tag.Types
  const { navigation } = useNavigation()
  const routeParams = {
    id: 'time_tracking',
    settingId: attendanceSetting?.attributes.id.toString() ?? 'default',
    tabId: 'edit',
  }
  const route = navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams)

  const stopPropagation = (e: MouseEvent) => e.stopPropagation()

  return (
    <Box
      aria-label="Time Tracking Policy"
      flexDirection="row"
      justifyContent="spaceBetween"
      width="full"
      extra={{ 'data-intercom-target': 'attendance-policy-card' }}
    >
      <Box flexDirection="column" gap="s12">
        <Box flexDirection="column" gap="s4">
          <Text.Normal weight="semibold">{name}</Text.Normal>
          <Text.Normal ellipsis size="200">
            {i18n.tp('time_tracking.settings.employees_assigned', {
              count: activeEmployees.length,
            })}
          </Text.Normal>
        </Box>
        {(defaultPolicy || attendanceSetting?.hasFlagsEnabled) && (
          <Box flexDirection="row" gap="s4" width="s300">
            {defaultPolicy && (
              <Tag type={radical} solid>
                {i18n.t('time_tracking.settings.tags.default')}
              </Tag>
            )}
            {desktopClockin && (
              <Tag type={grey}>{i18n.t('time_tracking.settings.tags.desktop')}</Tag>
            )}
            {attendanceSetting?.hasMobileClockin && (
              <Tag type={grey}>{i18n.t('time_tracking.settings.tags.mobile')}</Tag>
            )}
            {qrCodeClockin && <Tag type={grey}>{i18n.t('time_tracking.settings.tags.qr')}</Tag>}
            {geolocation && (
              <Tag type={grey}>{i18n.t('time_tracking.settings.tags.geolocation')}</Tag>
            )}
            {faceRecognition && (
              <Tag type={grey}>{i18n.t('time_tracking.settings.tags.face_recognition')}</Tag>
            )}
            {idClockSharedDevice && (
              <Tag type={grey}>{i18n.t('time_tracking.settings.tags.id_clock_shared_device')}</Tag>
            )}
          </Box>
        )}
      </Box>
      <Box flexDirection="row" gap="s6">
        <Box gap="s8" alignItems="center" flexDirection="row">
          <Box onClick={stopPropagation}>
            <Popover type="list">
              <Popover.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </Popover.Trigger>
              <Popover.Content side="bottom" align="end">
                {!defaultPolicy && (
                  <>
                    <Popover.TouchableOption
                      title={i18n.t('time_tracking.settings.set_as_default')}
                      onClick={submitSetAsDefault(policy)}
                    />
                    <Popover.TouchableOption
                      title={i18n.t('time_tracking.settings.delete')}
                      onClick={showDeleteModal}
                    />
                  </>
                )}
                {!!defaultPolicy && (
                  <Box padding="s24" gap="s4" textAlign="left">
                    <Text color="grey800" weight="medium" size="300">
                      {i18n.t('time_tracking.settings.remove.title')}
                    </Text>
                    <Text.Normal color="grey800" size="200">
                      {i18n.t('time_tracking.settings.remove.description')}
                    </Text.Normal>
                  </Box>
                )}
              </Popover.Content>
            </Popover>
          </Box>
        </Box>
        <Box alignSelf="center">
          <Button.Router
            aria-label="Go to"
            to={route}
            type="secondary"
            iconName="arrow-right"
            extra={{ 'data-intercom-target': 'attendance-policy-card-go-to-button' }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(PolicyCard)
